import { fetchUsers } from './usersService';
import { User } from './usersStore';

const USER_CACHE_KEY = 'telagri_user_cache';
const USER_CACHE_EXPIRY_KEY = 'telagri_user_cache_expiry';
const DEFAULT_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface CachedUserData {
	user: User;
	timestamp: number;
}

export function setUserCache(user: User, durationInMs: number = DEFAULT_CACHE_DURATION): void {
	if (!user) return;

	const cacheData: CachedUserData = {
		user,
		timestamp: Date.now(),
	};

	localStorage.setItem(USER_CACHE_KEY, JSON.stringify(cacheData));
	localStorage.setItem(USER_CACHE_EXPIRY_KEY, String(Date.now() + durationInMs));
}

export function getUserCache(): User | null {
	const cacheData = localStorage.getItem(USER_CACHE_KEY);
	const expiryTimestamp = localStorage.getItem(USER_CACHE_EXPIRY_KEY);

	if (!cacheData || !expiryTimestamp) {
		return null;
	}

	const expiryTime = parseInt(expiryTimestamp, 10);

	if (Date.now() > expiryTime) {
		clearUserCache();
		return null;
	}

	try {
		const parsedData: CachedUserData = JSON.parse(cacheData);
		return parsedData.user;
	} catch (error) {
		console.error('Error parsing cached user data:', error);
		clearUserCache();
		return null;
	}
}

export function clearUserCache(): void {
	localStorage.removeItem(USER_CACHE_KEY);
	localStorage.removeItem(USER_CACHE_EXPIRY_KEY);
}

export function isUserCacheValid(): boolean {
	const expiryTimestamp = localStorage.getItem(USER_CACHE_EXPIRY_KEY);

	if (!expiryTimestamp) {
		return false;
	}

	const expiryTime = parseInt(expiryTimestamp, 10);
	return Date.now() <= expiryTime;
}

export async function checkAndRefreshCacheIfNeeded(
	thresholdInMs: number = 5 * 60 * 1000,
	forceRefresh: boolean = false
): Promise<void> {
	const expiryTimestamp = localStorage.getItem(USER_CACHE_EXPIRY_KEY);

	if (forceRefresh) {
		try {
			const freshUserData = await fetchUsers();

			setUserCache(freshUserData);
			return;
		} catch (error) {
			console.error('Failed to refresh user cache:', error);
			return;
		}
	}

	if (!expiryTimestamp) {
		return;
	}

	const expiryTime = parseInt(expiryTimestamp, 10);
	const timeUntilExpiry = expiryTime - Date.now();

	if (timeUntilExpiry > 0 && timeUntilExpiry < thresholdInMs) {
		try {
			const freshUserData = await fetchUsers();

			setUserCache(freshUserData);
		} catch (error) {
			console.error('Failed to refresh user cache:', error);
		}
	}
}
